/**
 * MontaTech v2 - useProfiles Hook
 * 
 * Gerencia o estado dos perfis (API + localStorage)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApiCache } from '../../../../lib/useApiCache';
import { sendToTerminal } from '../../../../lib/terminalLogger';
import type { MontaTechProfile, ProfilesApiResponse } from '../types';
import { API_ENDPOINTS, API_CACHE_TTL, DEFAULT_PROFILE } from '../constants';
import {
  loadLocalProfiles,
  addLocalProfile,
  deleteLocalProfile,
  loadLastProfileName,
  saveLastProfileName,
  mergeProfiles,
  generateUniqueName
} from '../services/profileApi';

export interface UseProfilesReturn {
  // Estado
  profiles: MontaTechProfile[];
  activeProfiles: MontaTechProfile[];
  selectedProfile: MontaTechProfile | null;
  selectedProfileName: string;
  editingProfile: MontaTechProfile;
  isLoading: boolean;
  error: string | null;
  
  // Derivados
  apiProfiles: MontaTechProfile[];
  localProfiles: MontaTechProfile[];
  isLocalProfile: boolean;
  
  // Actions
  selectProfile: (name: string) => void;
  updateEditingProfile: (updates: Partial<MontaTechProfile>) => void;
  saveAsLocalProfile: (name?: string) => void;
  deleteProfile: (name: string) => void;
  refreshProfiles: () => void;
}

export function useProfiles(): UseProfilesReturn {
  // API profiles
  const { 
    data: apiResponse, 
    loading: apiLoading, 
    error: apiError,
    refresh: refreshApi,
    hasAttempted
  } = useApiCache<ProfilesApiResponse>(
    'montatech-profiles',
    API_ENDPOINTS.PROFILES,
    { ttlMs: API_CACHE_TTL.PROFILES }
  );

  // Log API errors
  useEffect(() => {
    if (hasAttempted && apiError) {
      sendToTerminal('❌ [MONTATECH/PROFILES] Erro ao carregar perfis', {
        error: apiError,
        endpoint: API_ENDPOINTS.PROFILES
      });
    }
  }, [apiResponse, apiError, hasAttempted]);

  // Local state
  const [localProfiles, setLocalProfiles] = useState<MontaTechProfile[]>(() => loadLocalProfiles());
  const [selectedProfileName, setSelectedProfileName] = useState<string>('');
  const [editingProfile, setEditingProfile] = useState<MontaTechProfile>(DEFAULT_PROFILE);

  // Derived state
  const apiProfiles = useMemo(() => {
    return (apiResponse?.data?.profiles || []).map(p => ({ ...p, isLocal: false }));
  }, [apiResponse]);

  const activeApiProfiles = useMemo(() => {
    return apiProfiles.filter(profile => profile.enabled !== false);
  }, [apiProfiles]);

  const activeLocalProfiles = useMemo(() => {
    return localProfiles.filter(profile => profile.enabled !== false);
  }, [localProfiles]);

  const allProfiles = useMemo(() => {
    return mergeProfiles(apiProfiles, localProfiles);
  }, [apiProfiles, localProfiles]);

  const activeProfiles = useMemo(() => {
    return mergeProfiles(activeApiProfiles, activeLocalProfiles);
  }, [activeApiProfiles, activeLocalProfiles]);

  const selectedProfile = useMemo(() => {
    return allProfiles.find(p => p.name === selectedProfileName) || null;
  }, [allProfiles, selectedProfileName]);

  const isLocalProfile = useMemo(() => {
    return selectedProfile?.isLocal === true;
  }, [selectedProfile]);

  // Inicializar seleção
  useEffect(() => {
    if (activeProfiles.length === 0) return;

    // Tentar restaurar última seleção
    const lastProfile = loadLastProfileName();
    const profileExists = activeProfiles.some(p => p.name === lastProfile);

    if (lastProfile && profileExists) {
      setSelectedProfileName(lastProfile);
      const profile = activeProfiles.find(p => p.name === lastProfile);
      if (profile) {
        setEditingProfile({ ...profile });
      }
    } else if (!selectedProfileName && activeProfiles.length > 0) {
      // Selecionar primeiro
      const first = activeProfiles[0];
      setSelectedProfileName(first.name);
      setEditingProfile({ ...first });
    }
  }, [activeProfiles]);

  // Actions
  const selectProfile = useCallback((name: string) => {
    const profile = activeProfiles.find(p => p.name === name);
    if (profile) {
      setSelectedProfileName(name);
      setEditingProfile({ ...profile });
      saveLastProfileName(name);
    }
  }, [activeProfiles]);

  const updateEditingProfile = useCallback((updates: Partial<MontaTechProfile>) => {
    setEditingProfile(prev => ({ ...prev, ...updates }));
  }, []);

  const saveAsLocalProfile = useCallback((name?: string) => {
    const profileName = name?.trim() || generateUniqueName(activeProfiles);
    
    const newProfile: MontaTechProfile = {
      ...editingProfile,
      name: profileName,
      isLocal: true
    };

    const updated = addLocalProfile(newProfile);
    setLocalProfiles(updated);
    setSelectedProfileName(profileName);
    setEditingProfile({ ...newProfile });
    saveLastProfileName(profileName);
  }, [editingProfile, activeProfiles]);

  const deleteProfile = useCallback((name: string) => {
    const profile = allProfiles.find(p => p.name === name);
    
    // Só pode deletar perfis locais
    if (!profile?.isLocal) return;

    const updated = deleteLocalProfile(name);
    setLocalProfiles(updated);

    // Se deletou o selecionado, selecionar outro
    if (selectedProfileName === name && activeProfiles.length > 1) {
      const remaining = activeProfiles.filter(p => p.name !== name);
      if (remaining.length > 0) {
        selectProfile(remaining[0].name);
      }
    }
  }, [allProfiles, activeProfiles, selectedProfileName, selectProfile]);

  const refreshProfiles = useCallback(() => {
    refreshApi();
    setLocalProfiles(loadLocalProfiles());
  }, [refreshApi]);

  return {
    // Estado
    profiles: allProfiles,
    activeProfiles,
    selectedProfile,
    selectedProfileName,
    editingProfile,
    isLoading: apiLoading,
    error: apiError,
    
    // Derivados
    apiProfiles,
    localProfiles,
    isLocalProfile,
    
    // Actions
    selectProfile,
    updateEditingProfile,
    saveAsLocalProfile,
    deleteProfile,
    refreshProfiles
  };
}

